import {useState} from 'react'

import PreviousActive from '../../images/icons/new-dashboard/previous-active.svg'
import PreviousInactive from '../../images/icons/new-dashboard/previous-inactive.svg'
import NextActive from '../../images/icons/new-dashboard/next-active.svg'
import NextInactive from '../../images/icons/new-dashboard/next-inactive.svg'

import TrackInProgress from '../../images/icons/new-dashboard/track-in-progress.svg'
import TrackComplete from '../../images/icons/new-dashboard/track-complete.svg'
import TrackIncomplete from '../../images/icons/new-dashboard/track-incomplete.svg'
import TrackInactive from '../../images/icons/new-dashboard/track-inactive.svg'
import {StudyGuide, StudyGuideStudyDays} from '../../redux/studyGuides'
import {
  AlertCount,
  DailyProgressBox,
  DayTrackerImage,
  DayTrackerItem,
  DayTrackerList,
  DayTrackerText,
  ImageButton,
} from './styles'
import {useWindowDimensions} from '../../hooks/useWindowDimensions'

type Props = {
  studyGuide?: StudyGuide
  expectedDay: number
  onClickOnDay: (index: number, studyDay: number) => void
}

export default function View({studyGuide, expectedDay, onClickOnDay}: Props) {
  const {isMobile, width} = useWindowDimensions()

  const currentDay = studyGuide?.assets?.find(a => a.inProgress)
  const finalExpectedDay =
    (currentDay?.studyDay ?? 1) > expectedDay
      ? currentDay?.studyDay ?? 1
      : expectedDay
  const days = studyGuide?.assets

  const getPerPage = () => {
    if (isMobile) return 3
    if (width < 1209) return 5
    if (width < 1380) return 7
    return 10
  }

  const perPage = getPerPage()

  const expectedIndex = days?.findIndex(
    item => item.studyDay === finalExpectedDay,
  )
  const initialPage =
    expectedIndex !== -1 ? Math.floor((expectedIndex ?? 0) / perPage) + 1 : 1

  const [currentPage, setCurrentPage] = useState(initialPage)
  const totalPages = Math.ceil((days?.length ?? 0) / perPage)

  const handleNext = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages))
  }

  const handlePrev = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1))
  }

  const currentItems = days?.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage,
  )

  const previousItems = days?.slice(0, (currentPage - 1) * perPage)
  const futureItems = days?.slice(currentPage * perPage)

  const incompletePreviousCount = previousItems?.filter(
    day => day.studyDay < finalExpectedDay && !day.isComplete,
  ).length

  const incompleteFutureCount = futureItems?.filter(
    day => day.studyDay < finalExpectedDay && !day.isComplete,
  ).length

  const getStatus = (day: StudyGuideStudyDays) => {
    if (day.studyDay < finalExpectedDay && !day.isComplete) {
      return 'behind'
    }

    if (day.studyDay === finalExpectedDay && !day.isComplete) {
      return 'track'
    }

    if (day.studyDay <= finalExpectedDay && day.isComplete) {
      return 'complete'
    }

    return 'inactive'
  }

  const getDayIcon = (day: StudyGuideStudyDays) => {
    if (day.isComplete) {
      return TrackComplete
    }

    if (day.studyDay === finalExpectedDay && !day.isComplete) {
      return TrackInProgress
    }

    if (day.studyDay < finalExpectedDay && !day.isComplete) {
      return TrackIncomplete
    }

    return TrackInactive
  }

  const handleClickOnDay = (day: StudyGuideStudyDays) => {
    onClickOnDay(day.studyDay - 1, day.studyDay)
  }

  return (
    <DailyProgressBox>
      <div style={{position: 'relative'}}>
        {(incompletePreviousCount ?? 0) > 0 && (
          <AlertCount oriented="left">{incompletePreviousCount}</AlertCount>
        )}
        <ImageButton onClick={handlePrev}>
          <img
            src={currentPage === 1 ? PreviousInactive : PreviousActive}
            alt="Previous Page"
          />
        </ImageButton>
      </div>
      <DayTrackerList>
        {currentItems?.map((item, key) => {
          const itemPosition =
            key === 0
              ? 'first'
              : key > 0 && key + 1 < currentItems.length
              ? 'center'
              : 'last'

          return (
            <DayTrackerItem onClick={() => handleClickOnDay(item)}>
              <DayTrackerImage
                itemPosition={itemPosition}
                itemStatus={getStatus(item)}
                key={`tracker-${key}`}
              >
                <img src={getDayIcon(item)} alt="Day Tracker" />
              </DayTrackerImage>
              <DayTrackerText $isComplete={item.isComplete}>
                Day {item.studyDay}
              </DayTrackerText>
            </DayTrackerItem>
          )
        })}
      </DayTrackerList>
      <div style={{position: 'relative'}}>
        {(incompleteFutureCount ?? 0) > 0 && (
          <AlertCount oriented="right">{incompleteFutureCount}</AlertCount>
        )}
        <ImageButton onClick={handleNext}>
          <img
            src={currentPage === totalPages ? NextInactive : NextActive}
            alt="Next Page"
          />
        </ImageButton>
      </div>
    </DailyProgressBox>
  )
}
